import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { renderDpatEvidenceLink, isEvidenceLinkUploaded } from "utils/renderDpatEvidenceLink";

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const SCORE_THRESHOLD_PCT = 70;

function hasEnoughEvidenceCoverage(decisions = []) {
  const rows = decisions ?? [];
  if (rows.length === 0) {
    return false;
  }

  const withEvidence = rows.filter((item) => isEvidenceLinkUploaded(item?.evidence)).length;
  return (withEvidence / rows.length) * 100 >= SCORE_THRESHOLD_PCT;
}

const GeneralAssemblyManagementActions = forwardRef(({
  year,
  decisions,
  managementActionServiceDeliveryData,
  districtId,
  hideComment,
  savedAssessmentData,
  isAssessmentStarted,
}, ref) => {

  const [maxScore, setMaxScore] = useState(2);

  const resolved = useMemo(() => {
    if (!isAssessmentStarted || !savedAssessmentData) return null;
    return {
      decisions: savedAssessmentData.decisions ?? decisions,
      managementActionServiceDeliveryData:
        savedAssessmentData.managementActionServiceDeliveryData ?? managementActionServiceDeliveryData,
      maxScore: savedAssessmentData.maxScore ?? 2,
      score: savedAssessmentData.score ?? 0,
    };
  }, [isAssessmentStarted, savedAssessmentData, decisions, managementActionServiceDeliveryData]);

  const displayDecisions = resolved?.decisions ?? decisions;
  const displayManagement = resolved?.managementActionServiceDeliveryData ?? managementActionServiceDeliveryData;
  const displayMaxScore = resolved?.maxScore ?? maxScore;

  const managementPct = Number(displayManagement?.[0]?.percentage);
  const implementationMet = Number.isFinite(managementPct) && managementPct >= SCORE_THRESHOLD_PCT;
  const evidenceCoverageMet = hasEnoughEvidenceCoverage(displayDecisions);
  const baseScore = implementationMet && evidenceCoverageMet ? 2 : 0;
  const displayScore = resolved?.score ?? (displayManagement?.length ? baseScore : 0);

 useImperativeHandle(ref, () => ({
     getData: () => ({
      indicator: "SDI1",
      area: "Management Coordination – Implementation of Service Delivery Decisions",
      maxScore: displayMaxScore,
      decisions: displayDecisions,
      managementActionServiceDeliveryData: displayManagement,
      score: displayScore,
     }),
   }));

  const DPAT_SLUG = "sdi1.0-1.2";

  const managementServiceDeliveryActionColumns = [
    { title: "No. of decisions on service delivery improvement", dataIndex: "no", key: "no" },
    { title: "No. of actions taken on social service improvement decisions", dataIndex: "service", key: "service" },
    { title: "% of service delivery improvement decisions implemented ", dataIndex: "percentage", key: "percentage" }
  ];

  const decisionColumns = [
    { title: "Service delivery improvement issues", dataIndex: "decision", key: "decision" },
    { title: "Actions taken", dataIndex: "action", key: "action" },
    {
      title: "Attached evidence",
      dataIndex: "evidence",
      key: "evidence",
      render: (value) => renderDpatEvidenceLink(value, "View Evidence"),
    },
  ];

  return (
    <Comment
      data={displayManagement}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi1.0-1.2-${year}`}
       hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={displayMaxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 1.0-1.2 Actual Score" score={displayManagement ? displayScore : "N/A"} />
          </IndicatorScoreSection>

          <Title level={5} style={{ marginTop: "30px" }}>I. Evidence of management actions on service delivery decisions</Title>
          {displayManagement?.length > 0 && <Table
            columns={managementServiceDeliveryActionColumns}
            dataSource={displayManagement}
            rowKey={(record, index) => record.key ?? index}
            pagination={false}
            bordered
          />}
          <Title level={5} style={{ marginTop: "30px" }}>II. Examples of actions taken on decisions</Title>
          {displayDecisions?.length > 0 && <Table
            columns={decisionColumns}
            dataSource={displayDecisions}
            rowKey={(record) => record.key ?? record.decision}
            pagination={false}
            bordered
          />}

          <Title level={5} style={{ marginTop: "30px" }}>Conclusion</Title>
          <Content>
            {Number.isFinite(managementPct) ? managementPct : 0} % of the total no. of decisions on improving service delivery were implemented in {year}.
          </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default GeneralAssemblyManagementActions;